import React, { ReactNode, PureComponent, MouseEvent } from 'react'
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { CSSTransition } from 'react-transition-group'

import { Button } from '@components/Button'
import { ChevronLeftIcon, PlusIcon, MinusSignIcon } from '@components/Icons'
import {
  ExpanderWrapper,
  Heading,
  ChevronIconWrapper,
  ExpandedContent,
  PlusIconWrapper,
  TRANSITION_DELAY,
  chevronBtnStyles,
} from './Expander.elements'

type Props = {
  isOpen: boolean
  expandedContent: ReactNode
  label?: string
  children: ReactNode
  onToggle: () => void
  className?: string
  usePlusIcon?: boolean
}

export class Expander extends PureComponent<Props> {
  static defaultProps = {
    usePlusIcon: false
  }

  content?: HTMLElement

  setContentRef = (element: HTMLDivElement | null) => {
    this.content = element || undefined
  }

  removeContentHeight = () => {
    const content = this.content
    if (!content) return

    content.style.height = ''
  }

  setContentHeight = () => {
    const content = this.content
    if (!content) return

    content.style.height = content.scrollHeight + 'px'
  }

  handleToggle = (event: MouseEvent<any>) => {
    event.preventDefault()
    event.stopPropagation()
    this.props.onToggle()
  }

  renderExpandedContent = (state: 'entered' | 'exited') => {
    const { isOpen, expandedContent } = this.props

    return (
      <ExpandedContent
        aria-hidden={!isOpen}
        ref={this.setContentRef}
        state={state}
      >
        {expandedContent}
      </ExpandedContent>
    )
  }

  renderChevron() {
    const { isOpen, label, usePlusIcon } = this.props

    return (
      <Button
        onClick={this.handleToggle}
        aria-expanded={isOpen}
        aria-label={label || 'Section'}
        variant="text"
        circled
        css={chevronBtnStyles}
      >
        {usePlusIcon
          ? (
            <PlusIconWrapper>
              {isOpen
                ? <MinusSignIcon />
                : <PlusIcon />
              }
            </PlusIconWrapper>
          ): (
            <ChevronIconWrapper isOpen={isOpen}>
              <ChevronLeftIcon />
            </ChevronIconWrapper>
          )
        }
      </Button>
    )
  }

  render() {
    const { children, isOpen, className } = this.props

    return (
      <ExpanderWrapper>
        <div>
          <Heading
            className={className}
            onClick={this.handleToggle}
          >
            {children}
            {this.renderChevron()}
          </Heading>
        </div>

        <CSSTransition
          in={isOpen}
          onEnter={this.setContentHeight}
          onEntered={this.removeContentHeight}
          onExit={this.setContentHeight}
          onExiting={this.removeContentHeight}
          timeout={TRANSITION_DELAY}
        >
          {this.renderExpandedContent}
        </CSSTransition>
      </ExpanderWrapper>
    )
  }
}
